






















































import { defineComponent, PropType } from '@nuxtjs/composition-api';
import BaseButton from '@shared/components/@atoms/BaseButton/BaseButton.vue';
import UCContainer from '@shared/components/@atoms/UCContainer/UCContainer.vue';
import BaseNotification from '@shared/components/@molecules/BaseNotification/BaseNotification.vue';

import ContactFormField from './internal/ContactFormField.vue';
import { IContactFormField, IEmailBody, IFormInput, ISubmitPayload } from './types';

export default defineComponent({
  name: 'UCContactForm',
  components: {
    UCContainer,
    ContactFormField,
    BaseNotification,
    BaseButton,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    // eslint-disable-next-line vue/prop-name-casing
    buttonText: {
      type: String,
      required: true,
    },
    fields: {
      type: Array as PropType<IContactFormField[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    error: {
      type: Error,
      required: true,
    },
    response: {
      type: Object,
      default: null,
    },
  },
  data() {
    const userInput: IFormInput = {};
    const displayFields: string[] = [];
    const errorFields: string[] = [];

    return {
      userInput,
      displayFields,
      errorFields,
      hasNotification: false,
      isFormEnabled: false,
      shouldResetChildren: false,
    };
  },
  head() {
    return {
      script: [
        {
          id: 'google-recaptcha',
          src: `https://www.google.com/recaptcha/api.js?render=${
            (this as any).$config.recaptchaSiteKey
          }`,
        },
      ],
    };
  },
  computed: {
    notificationType(): string {
      if (this.error) {
        return 'danger';
      } else if (this.response) {
        return 'success';
      }
      return '';
    },
    notificationMessage(): string {
      if (this.error) {
        return `Something went wrong! Please try again later.`;
      } else if (this.response) {
        return "Thanks for contacting us. We'll get back to you as soon as possible.";
      } else {
        return '';
      }
    },
  },
  watch: {
    loading(newValue, prevValue) {
      if (prevValue === true && newValue === false) {
        this.$data.hasNotification = true;
      }
    },
    hasNotification(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.$data.hasNotification = false;
        }, 10000);
      }
    },
  },
  methods: {
    resetComplete() {
      this.shouldResetChildren = false;
    },
    widthClass(field: IContactFormField): string {
      return field.width === 'half' ? 'half-width' : 'full-width';
    },
    isInDisplayFields(id: string | number): boolean {
      // @ts-ignore migrated from ui-library
      return this.displayFields.includes(id);
    },
    updateDisplayFields(displayFields: string[]) {
      this.displayFields = displayFields;
    },
    updateInputValues(inputField: IContactFormField) {
      if (inputField.type === 'enquiry_type') {
        const keysToRemove = [];
        // @ts-ignore migrated from ui-library
        for (const [key, value] of Object.entries(this.userInput)) {
          const input = value as IContactFormField;
          if ((input.isEnquirySpecific && input.type !== 'enquiry_type') || !input.type) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach((key) => {
          // @ts-ignore migrated from ui-library
          delete this.userInput[key];
        });
      }

      if (inputField.value) {
        // @ts-ignore migrated from ui-library
        this.errorFields = this.errorFields.filter((field: string) => field !== inputField.id);
      } else {
        // @ts-ignore migrated from ui-library
        if (this.isFieldRequired(inputField)) {
          // @ts-ignore migrated from ui-library
          this.errorFields.push(inputField.id as string);
        }
      }

      if (inputField.mappedLabel) {
        // @ts-ignore migrated from ui-library
        this.userInput[inputField.mappedLabel] = inputField;
      }
    },
    isFieldRequired(field: IContactFormField) {
      return (
        (!field.isEnquirySpecific && field.isRequired) ||
        // @ts-ignore migrated from ui-library
        (this.isInDisplayFields(field.id as string) && field.isRequired)
      );
    },
    submitForm() {
      this.hasNotification = false;
      const requiredFields = this.fields.filter((field: IContactFormField) =>
        // @ts-ignore migrated from ui-library
        this.isFieldRequired(field),
      );

      // @ts-ignore migrated from ui-library
      const fieldIds: string[] = requiredFields.map((field: IContactFormField) => field.id);
      // @ts-ignore migrated from ui-library
      const inputIds = Object.values(this.userInput).map(
        (field) => (field as IContactFormField | null)?.id,
      );
      this.errorFields = fieldIds.filter((id) => !inputIds.includes(id));
      // @ts-ignore migrated from ui-library
      if (this.errorFields.length) {
        return;
      } else {
        const grecaptcha = window.grecaptcha;

        const userInput = this.userInput;
        // @ts-ignore migrated from ui-library
        const siteKey = this.$config.recaptchaSiteKey;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        grecaptcha.ready(function () {
          grecaptcha.execute(siteKey, { action: 'submit' }).then(async function (token: string) {
            const emailBody: IEmailBody = {};
            let userEmail = '';

            // @ts-ignore migrated from ui-library
            for (const [key, value] of Object.entries(userInput)) {
              const input = value as IContactFormField;

              if (input.value) {
                if (input.type === 'email') {
                  userEmail = input.value;
                } else {
                  emailBody[key] = input.value;
                }
              }
            }

            const submitPayload: ISubmitPayload = {
              token,
              emailBody,
              userEmail,
            };
            self.$emit('on-submit', submitPayload);

            self.userInput = {};
            self.displayFields = [];
            self.isFormEnabled = false;
            (self.$refs.contactForm as HTMLFormElement).reset();
            self.shouldResetChildren = true;
          });
        });
      }
    },
    closeNotification() {
      this.hasNotification = false;
    },
    shouldShowField(field: IContactFormField): boolean {
      return !!(field.visibleOnStart || this.isFormEnabled);
    },
    resetContactForm() {
      this.isFormEnabled = false;
      this.errorFields = [];
      this.displayFields = [];
      // (this.$refs.contactForm as HTMLFormElement).reset();
    },
    resetContactFormPartial() {
      this.errorFields = [];
      const keysToRemove = [];
      // @ts-ignore migrated from ui-library
      for (const [key, value] of Object.entries(this.userInput)) {
        const input = value as IContactFormField;
        if (
          input.isEnquirySpecific &&
          // @ts-ignore migrated from ui-library
          !this.isInDisplayFields(input.id as string) &&
          input.type !== 'enquiry_type' &&
          input.type
        ) {
          // when switching the fields based on leaf enquiry type, remove existing but irrelevant fields from userInput
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        // @ts-ignore migrated from ui-library
        delete this.userInput[key];
      });
    },
    enableOtherFields(shouldEnable: boolean) {
      this.isFormEnabled = shouldEnable;
    },
  },
});
